import React from "react";
import { motion } from "framer-motion";
import { FaEnvelope, FaPhoneAlt, FaMapMarkerAlt, FaPaperPlane } from "react-icons/fa";
import { HiOutlineMail, HiOutlineUser } from "react-icons/hi";
import { MdMessage } from "react-icons/md";

function ContactModern() {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10,
      },
    },
  };

  const imageVariants = {
    hidden: { scale: 1.1, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 1.2,
        ease: "easeOut",
      },
    },
  };

  const formVariants = {
    hidden: { x: -50, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className="w-full min-h-screen flex items-center justify-center px-3 sm:px-4 py-6 sm:py-8 bg-gradient-to-br from-[#f0f3f1] to-[#e8efe9]"
    >
      <motion.div
        variants={containerVariants}
        className="max-w-7xl w-full bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col lg:grid lg:grid-cols-2 border border-white/20"
      >
        {/* LEFT FORM SIDE */}
        <motion.div
          variants={formVariants}
          className="p-5 sm:p-8 md:p-10 lg:p-12 xl:p-16 bg-gradient-to-br from-white to-[#f5f9f6] order-2 lg:order-1"
        >
          <motion.div variants={itemVariants}>
            <span className="inline-block px-3 sm:px-4 py-1 sm:py-1.5 mb-4 sm:mb-6 text-xs font-semibold tracking-wider text-[#0a2508] uppercase bg-green-100 rounded-full">
              Get In Touch
            </span>
          </motion.div>

          <motion.h2
            variants={itemVariants}
            className="text-3xl sm:text-4xl md:text-5xl font-light text-gray-800 mb-3 sm:mb-4 tracking-tight"
          >
            Let's Create
            <span className="block font-semibold text-[#12AF09] mt-1 sm:mt-2">
              Something Beautiful
            </span>
          </motion.h2>

          <motion.p
            variants={itemVariants}
            className="text-sm sm:text-base md:text-lg text-gray-500 mb-6 sm:mb-8 md:mb-10 leading-relaxed"
          >
            I'm always excited to hear about new projects and opportunities. 
            Drop me a line and let's bring your ideas to life.
          </motion.p>

          <motion.form
            variants={containerVariants}
            className="space-y-4 sm:space-y-5 md:space-y-6"
          >
            {/* Name Input */}
            <motion.div variants={itemVariants} className="relative">
              <HiOutlineUser className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-[#12AF09] text-lg sm:text-xl" />
              <input
                type="text"
                placeholder="Your Full Name"
                className="w-full border-b-2 border-gray-200 bg-transparent pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-4 text-sm sm:text-base md:text-lg text-gray-700 outline-none transition-all duration-300 focus:border-[#12AF09] placeholder-gray-400"
              />
            </motion.div>

            {/* Email Input */}
            <motion.div variants={itemVariants} className="relative">
              <HiOutlineMail className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-[#12AF09] text-lg sm:text-xl" />
              <input
                type="email"
                placeholder="Email Address"
                className="w-full border-b-2 border-gray-200 bg-transparent pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-4 text-sm sm:text-base md:text-lg text-gray-700 outline-none transition-all duration-300 focus:border-[#12AF09] placeholder-gray-400"
              />
            </motion.div>

            {/* Message Input */}
            <motion.div variants={itemVariants} className="relative">
              <MdMessage className="absolute left-3 sm:left-4 top-5 sm:top-6 text-[#12AF09] text-lg sm:text-xl" />
              <textarea
                rows="4"
                placeholder="Your Message"
                className="w-full border-b-2 border-gray-200 bg-transparent pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 sm:py-4 text-sm sm:text-base md:text-lg text-gray-700 outline-none transition-all duration-300 focus:border-[#12AF09] placeholder-gray-400 resize-none"
              ></textarea>
            </motion.div>

            {/* Submit Button */}
            <motion.div variants={itemVariants} className="pt-2 sm:pt-3 md:pt-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="group relative overflow-hidden bg-[#12AF09] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-medium text-base sm:text-lg shadow-xl hover:shadow-2xl transition-all duration-300 w-full sm:w-auto"
              >
                <span className="relative z-10 flex items-center justify-center gap-2 sm:gap-3">
                  Send Message
                  <FaPaperPlane className="text-xs sm:text-sm transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </span>
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-[#066d1f] to-[#088833]"
                  initial={{ x: "100%" }}
                  whileHover={{ x: 0 }}
                  transition={{ duration: 0.3 }}
                />
              </motion.button>
            </motion.div>
          </motion.form>

          {/* CONTACT INFO */}
          <motion.div
            variants={containerVariants}
            className="mt-8 sm:mt-10 md:mt-12 pt-6 sm:pt-8 border-t border-gray-200"
          >
            <motion.h4
              variants={itemVariants}
              className="font-medium text-gray-400 mb-4 sm:mb-6 tracking-wider text-xs sm:text-sm uppercase"
            >
              Contact Information
            </motion.h4>

            <div className="space-y-3 sm:space-y-4 md:space-y-5">
              {/* Email */}
              <motion.div
                variants={itemVariants}
                whileHover={{ x: 10 }}
                className="flex items-center gap-3 sm:gap-4 text-gray-600 group cursor-pointer"
              >
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-green-100 flex items-center justify-center group-hover:bg-[#12AF09] transition-colors duration-300 flex-shrink-0">
                  <FaEnvelope className="text-[#12AF09] text-xs sm:text-sm group-hover:text-white transition-colors duration-300" />
                </div>
                <span className="text-sm sm:text-base md:text-lg break-all">connnect@chainmagicianlab.com</span>
              </motion.div>

              {/* Phone */}
              <motion.div
                variants={itemVariants}
                whileHover={{ x: 10 }}
                className="flex items-center gap-3 sm:gap-4 text-gray-600 group cursor-pointer"
              >
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-green-100 flex items-center justify-center group-hover:bg-[#12AF09] transition-colors duration-300 flex-shrink-0">
                  <FaPhoneAlt className="text-[#12AF09] text-xs sm:text-sm group-hover:text-white transition-colors duration-300" />
                </div>
                <span className="text-sm sm:text-base md:text-lg">+91 63935 61379</span>
              </motion.div>

              {/* Address */}
              <motion.div
                variants={itemVariants}
                whileHover={{ x: 10 }}
                className="flex items-start gap-3 sm:gap-4 text-gray-600 group cursor-pointer"
              >
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-green-100 flex items-center justify-center group-hover:bg-[#12AF09] transition-colors duration-300 flex-shrink-0 mt-1">
                  <FaMapMarkerAlt className="text-[#12AF09] text-xs sm:text-sm group-hover:text-white transition-colors duration-300" />
                </div>
                <span className="text-sm sm:text-base md:text-lg leading-relaxed">
                  Chain Magician Lab Pvt Ltd
                  <br />
                  Shantipuram, Phaphamau
                  <br />
                  Uttar Pradesh, India 211013
                </span>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>

        {/* RIGHT IMAGE SIDE */}
        <motion.div
          variants={imageVariants}
          className="relative h-56 xs:h-56 sm:h-64 md:h-80 lg:h-auto overflow-hidden group order-1 lg:order-2"
        >
          {/* Overlay with green tint */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#12AF09]/30 to-transparent z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          
          {/* Image - using placeholder, replace with your actual image path */}
          <picture>
            <source
              media="(max-width: 1000px)"
              srcSet="./img/contact1.jpg"
            />
            <img
              src="./img/contact.jpg"
              alt="Premium workspace"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
          </picture>
          
          {/* Decorative Pattern */}
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2312AF09' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              backgroundRepeat: "repeat"
            }}
          />
          
          {/* Quote Overlay - hidden on mobile, visible on larger screens */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="absolute bottom-4 sm:bottom-8 left-4 sm:left-8 right-4 sm:right-8 z-20 hidden md:block"
          >
            <div className="bg-black/20 backdrop-blur-sm p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-xl border-l-4 border-[#12AF09]">
              <p className="text-white text-sm sm:text-base md:text-lg italic">
                "Design is not just what it looks like and feels like. 
                Design is how it works."
              </p>
              <p className="text-[#12AF09] font-medium mt-1 sm:mt-2 text-xs sm:text-sm md:text-base">
                — Steve Jobs
              </p>
            </div>
          </motion.div>
          
          {/* Mobile-friendly text overlay (optional) */}
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent md:hidden">
            <p className="text-white text-sm font-light">
              Let's create something amazing together
            </p>
          </div>
        </motion.div>
      </motion.div>
    </motion.section>
  );
}

export default ContactModern;